'use client';

import type { DecisionTreeErrorSceneProps } from './decision-tree-error-scene-props.types';
import { Button, buttonStyle } from '@florencelabs/button';
import { MaterialSymbol } from '@florencelabs/icons';
import { ModalCloseButton, DialogTitle } from '@florencelabs/modal-2';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ReactElement, useLayoutEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { LoadingGradient } from '@provider-directory/components/loading-gradient/loading-gradient.component';
import {
  formatPhone,
  PhoneLink,
} from '@provider-directory/components/phone-number';
import {
  useDecisionTree,
  setSchedulingFlowType,
  getSchedulingFlowType,
} from '@provider-directory/contexts/DecisionTreeContext';
import { useFeatureFlags } from '@provider-directory/contexts/FlagContext';
import { useProvider } from '@provider-directory/contexts/ProviderContext';
import { getProviderPrimaryPhoneNumber } from '@provider-directory/functions/get-available-phone';
import { useDecisionTreeConversionClick } from '@provider-directory/hooks/useDecisionTreeConversionClick';
import { ProviderResource } from '@provider-directory/types/providers.contracts';
import { useAnalytics } from '../analytics/analytics-provider';
import { getAvailableProvidersForRFV } from '../availability/availability.model';
import { ContactsList, NoBreak, Phone } from '../helpers';
import {
  useOrgContext,
  useOrgContextBasedOnHospitalAssociations,
} from '../org-context/org-context-provider';
import { DecisionTreeErrorButton } from './decision-tree-error-button';
import { RfvMismatchProviderCard } from './rfv-mismatch-provider-card';

interface NoProvidersContentProps {
  t: ReturnType<typeof useTranslation>['t'];
  phone: string;
  onConversionClick: (href: string) => void;
}

const NoProvidersContent = ({
  t,
  phone,
  onConversionClick,
}: NoProvidersContentProps): ReactElement => (
  <>
    <div className={'flex w-full pb-6 pt-10'}>
      <MaterialSymbol
        size={40}
        name={'error_fill'}
        className={'text-primary-color'}
      />
    </div>
    <div className={'flex w-full flex-col gap-3 pb-8 tablet:pb-10'}>
      <DialogTitle
        className={'font-serif text-display-xlarge'}
        data-testid={'rfv-mismatch-no-providers-title'}
      >
        {t('decisionTree.reasonForVisitProviderError.noProvidersTitle')}
      </DialogTitle>
      <p className={'text-body-medium text-neutral-700'}>
        <Trans
          t={t}
          i18nKey={'decisionTree.reasonForVisitProviderError.noProvidersBody'}
          components={{
            phone: <Phone onClick={onConversionClick} phone={phone} />,
          }}
        />
      </p>
    </div>
    <div className={'flex flex-col pb-5 tablet:flex-row'}>
      <PhoneLink
        phone={phone}
        data-testid={'rfv-mismatch-no-providers-call-btn'}
        className={buttonStyle({
          variant: 'clientMain',
          className: 'py-[10px]',
        })}
        aria-label={t('aria.callPhoneNumber', {
          phoneDisplay: formatPhone(phone),
        })}
        onClick={(e) => {
          onConversionClick(e.currentTarget.href);
        }}
      >
        {({ formattedPhone }) =>
          t('decisionTree.reasonForVisitProviderError.noProvidersCallCta', {
            phoneDisplay: formattedPhone,
          })
        }
      </PhoneLink>
    </div>
  </>
);

const LoadingContent = (): ReactElement => (
  <div className={'flex flex-col gap-3'}>
    <LoadingGradient className={'h-24 w-full rounded-lg'} />
    <LoadingGradient className={'h-24 w-full rounded-lg'} />
    <LoadingGradient className={'h-24 w-full rounded-lg'} />
  </div>
);

interface MatchingProvidersContentProps {
  t: ReturnType<typeof useTranslation>['t'];
  appointmentName: string | null;
  isLoading: boolean;
  shownProviders: ProviderResource[];
  totalProviders: number;
  onViewAllProviders: () => void;
}

const MatchingProvidersContent = ({
  t,
  appointmentName,
  isLoading,
  shownProviders,
  totalProviders,
  onViewAllProviders,
}: MatchingProvidersContentProps): ReactElement => (
  <>
    {/* Title */}
    <div className={'flex w-full flex-col gap-3 pb-8 pt-10 tablet:pb-6'}>
      <DialogTitle
        className={'font-serif text-display-xlarge'}
        data-testid={'rfv-mismatch-title'}
      >
        {t('decisionTree.reasonForVisitProviderError.title')}
      </DialogTitle>

      {/* Subtitle */}
      {appointmentName && (
        <p className={'text-body-medium text-neutral-700'}>
          <Trans
            t={t}
            i18nKey={'decisionTree.reasonForVisitProviderError.subtitle'}
            values={{ appointmentName }}
            components={{
              nobreak: <NoBreak />,
            }}
          />
        </p>
      )}
    </div>

    {/* Alternative Providers */}
    {isLoading ? (
      <LoadingContent />
    ) : (
      <div className={'flex flex-col gap-3 pb-8 tablet:pb-10'}>
        {shownProviders.map((altProvider) => (
          <RfvMismatchProviderCard
            key={altProvider.npi}
            provider={altProvider}
          />
        ))}
      </div>
    )}

    {/* View All Providers Button */}
    {!isLoading && totalProviders > 0 && (
      <div className={'flex flex-col tablet:flex-row'}>
        <Button
          variant={'clientMain'}
          className={'py-[10px]'}
          onPress={onViewAllProviders}
          isDisabled={isLoading}
          testId={'view-all-providers-btn'}
        >
          {t('decisionTree.reasonForVisitProviderError.viewProviders', {
            count: totalProviders,
          })}
        </Button>
      </div>
    )}
  </>
);

export const RfvMismatchModal = ({
  name,
  icon,
  code,
  title,
  body,
  specialty = '',
  ctaButtons = [],
}: DecisionTreeErrorSceneProps): ReactElement => {
  const { featureFlags } = useFeatureFlags();
  const { provider } = useProvider();
  const router = useRouter();
  const {
    key,
    name: healthSystemName,
    decisionTree: decisionTreeConfig,
  } = useOrgContextBasedOnHospitalAssociations(provider?.hospitalAssociations);
  const orgContext = useOrgContext();
  const { t } = useTranslation(key);
  const [isLoading, setIsLoading] = useState(false);
  const [shownProviders, setShownProviders] = useState<ProviderResource[]>([]);
  const [totalProviders, setTotalProviders] = useState(0);

  const { decisionTree } = useDecisionTree();

  const onConversionClick = useDecisionTreeConversionClick();
  const analytics = useAnalytics();
  const appointmentName = decisionTree.rfvKey
    ? t(`decisionTree.reasonForVisit.${decisionTree.rfvKey}.label`)
    : null;

  const { isRFVMismatchEnabled } = featureFlags;

  // Resolved from the ACTIVE directory (not the association-resolved sibling
  // above), consistent with how the MyChart scheduling URL is resolved.
  const myChartLoginUrl = orgContext.getMyChartLoginUrl(provider ?? undefined);

  // if a phone override exists on the config, use it
  const configPhone =
    decisionTreeConfig.scenesConfig[name]?.scenePhoneNumber ??
    decisionTreeConfig.mainPhoneNumber;

  const contactList =
    decisionTreeConfig.scenesConfig[name]?.contacts ??
    decisionTreeConfig.contacts ??
    [];

  useLayoutEffect(() => {
    const loadAlternativeProviders = async (): Promise<void> => {
      if (!decisionTree.rfvId) return;

      try {
        setIsLoading(true);
        const { availableProviders, totalProviders: totalCount } =
          await getAvailableProvidersForRFV(decisionTree.rfvId);
        setShownProviders(availableProviders.slice(0, 3));
        setTotalProviders(totalCount);
        setIsLoading(false);
      } catch {
        setIsLoading(false);
      }
    };

    loadAlternativeProviders();
  }, [decisionTree.rfvId]);

  const handleViewAllProviders = (): void => {
    setSchedulingFlowType('BookAnotherProvider', { sticky: true });
    router.push(`/providers/find?reason-for-visit=${decisionTree.rfvId}`, {
      scroll: false,
    });
  };

  return isRFVMismatchEnabled ? (
    <div
      className={
        'mx-auto flex w-full flex-col rounded-b-lg px-6 py-8 tablet:px-16 tablet:py-10 desktop:px-12'
      }
    >
      <div className={'flex flex-row justify-end'}>
        <ModalCloseButton className={'static'} ariaLabel={t('close')} />
      </div>

      {!isLoading && totalProviders <= 0 ? (
        <NoProvidersContent
          t={t}
          phone={configPhone}
          onConversionClick={onConversionClick}
        />
      ) : (
        <MatchingProvidersContent
          t={t}
          isLoading={isLoading}
          shownProviders={shownProviders}
          totalProviders={totalProviders}
          appointmentName={appointmentName}
          onViewAllProviders={handleViewAllProviders}
        />
      )}
    </div>
  ) : (
    <div
      className={
        'mx-auto flex w-full flex-col rounded-b-lg px-6 pb-16 pt-10 tablet:px-16 desktop:px-12'
      }
    >
      <div className={'flex flex-row justify-end'}>
        <ModalCloseButton className={'static'} ariaLabel={t('close')} />
      </div>
      {icon && (
        <div className={'flex w-full pb-6'}>
          <MaterialSymbol
            size={40}
            name={icon}
            className={'text-primary-950'}
          />
        </div>
      )}
      <div className={'flex w-full flex-col gap-3 pb-10'}>
        {code && <p className={'text-body-small-bold'}>{code.toUpperCase()}</p>}
        <DialogTitle
          className={'font-serif text-display-xlarge'}
          data-testid={'error-title-modal'}
        >
          {
            <Trans
              t={t}
              i18nKey={title}
              specialty={specialty}
              values={{
                specialty: t(specialty),
                healthSystem: healthSystemName,
                appointmentName,
                providerName: provider?.name.full,
              }}
              components={{
                nobreak: <NoBreak />,
              }}
            />
          }
        </DialogTitle>
        {isLoading ? (
          <LoadingGradient className={'h-[32px] w-full rounded-sm'} />
        ) : (
          <div>
            <Trans
              t={t}
              i18nKey={body}
              specialty={specialty}
              values={{
                specialty: t(specialty),
                healthSystem: healthSystemName,
                appointmentName,
                providerName: provider?.name.full,
                providerCount: totalProviders,
              }}
              components={{
                myChartLink: myChartLoginUrl ? (
                  <Link
                    onClick={() => {
                      analytics.ehrRedirectEvent({
                        linkContextPage: 'ProviderDetails',
                        linkContextComponent: 'DecisionTree',
                        provider: provider ?? undefined,
                        phoneNumber: getProviderPrimaryPhoneNumber(
                          provider,
                          orgContext.getAssociationPhoneSelection(),
                        ),
                        schedulingFlowType: getSchedulingFlowType(),
                        redirectUrl: myChartLoginUrl,
                      });
                    }}
                    className={'text-primary-color underline'}
                    href={myChartLoginUrl}
                    target={'_blank'}
                  />
                ) : (
                  // MyChart is not configured for this directory: keep the copy
                  // but render it inert instead of a dead link.
                  <span />
                ),
                nobreak: <NoBreak />,
                phone: (
                  <Phone onClick={onConversionClick} phone={configPhone} />
                ),
                contactsList: (
                  <ContactsList
                    onClick={onConversionClick}
                    contacts={contactList}
                  />
                ),
              }}
            />
          </div>
        )}
      </div>
      <div className={'flex flex-col gap-2 tablet:flex-row'}>
        {ctaButtons.map((button, index) => (
          <DecisionTreeErrorButton
            isDisabled={isLoading}
            type={button}
            variant={index === 0 ? 'clientMain' : 'systemOutline'}
            key={button}
          />
        ))}
      </div>
    </div>
  );
};
